import { ButtonPrimary, ButtonSecondary } from 'components/Button'
import { AutoColumn } from 'components/Column'
import Modal from 'components/Modal'
import { RowBetween } from 'components/Row'
import React from 'react'
import { AlertTriangle, ArrowRight, X } from 'react-feather'
import styled, { useTheme } from 'styled-components'
import { ThemedText } from 'theme'

const Wrapper = styled.div`
  width: 100%;
  border-radius: 20px;
  max-width: 100%;

  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 16px;
  }
`

const CloseIcon = styled.div`
  position: absolute;
  right: 20px;
  top: 20px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    cursor: pointer;
    background: ${({ theme }) => theme.deprecated_bg2};
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    right: 16px;
    top: 16px;
  }
`

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 24px;

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    gap: 12px;
  }
`

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`

const Circle = styled.div<{ color: string }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${({ color }) => color};
  opacity: 0.4;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 480px) {
    width: 52px;
    height: 52px;
  }
`

const StyledAlertTriangle = styled(AlertTriangle)`
  color: ${({ theme }) => theme.deprecated_yellow2};
`

const TitleContainer = styled.div`
  text-align: center;
  margin-bottom: 16px;

  @media (max-width: 480px) {
    margin-bottom: 12px;
  }
`

const ContentContainer = styled.div`
  background-color: ${({ theme }) => theme.cardSecondary};
  border-radius: 12px;
  padding: 16px;
  margin: 20px 0;

  @media (max-width: 480px) {
    padding: 12px;
    margin: 16px 0;
    border-radius: 10px;
  }
`

const StepContainer = styled.div`
  margin: 16px 0;

  @media (max-width: 480px) {
    margin: 12px 0;
  }
`

const StepItem = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 8px 0;

  @media (max-width: 480px) {
    padding: 6px 0;
  }
`

const StepNumber = styled.div<{ color: string }>`
  min-width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${({ color }) => color};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  margin-right: 12px;

  @media (max-width: 480px) {
    min-width: 24px;
    height: 24px;
    margin-right: 10px;
    font-size: 12px;
  }
`

const StepContent = styled.div`
  flex: 1;
`

const WarningBox = styled.div`
  background: ${({ theme }) => `${theme.deprecated_red1}10`};
  border-left: 4px solid ${({ theme }) => theme.deprecated_red1};
  padding: 12px 16px;
  border-radius: 8px;
  margin-top: 16px;

  @media (max-width: 480px) {
    padding: 10px 12px;
    margin-top: 12px;
  }
`

const StyledButtonPrimary = styled(ButtonPrimary)`
  &:hover {
    box-shadow: 0 6px 10px rgba(0, 0, 0, 0.15);
  }
  transition: all 0.2s ease;
  font-weight: 600;
  height: 48px;

  @media (max-width: 480px) {
    height: 44px;
  }
`

const StyledButtonSecondary = styled(ButtonSecondary)`
  &:hover {
    box-shadow: 0 6px 10px rgba(0, 0, 0, 0.15);
  }
  transition: all 0.2s ease;
  font-weight: 600;
  height: 48px;

  @media (max-width: 480px) {
    height: 44px;
  }
`

// Custom text components for better mobile responsiveness
const MobileResponsiveTitle = styled(ThemedText.DeprecatedLargeHeader)`
  @media (max-width: 480px) {
    font-size: 20px;
  }
`

const MobileResponsiveSubHeader = styled(ThemedText.DeprecatedSubHeader)`
  @media (max-width: 480px) {
    font-size: 16px;
  }
`

const MobileResponsiveBody = styled(ThemedText.DeprecatedBody)`
  @media (max-width: 480px) {
    font-size: ${(props) => {
      if (!props.fontSize) return '13px'
      if (typeof props.fontSize === 'number') {
        return `${Math.max(props.fontSize - 1, 12)}px`
      }
      return props.fontSize
    }};
  }
`

interface ConfirmationModalProps {
  isOpen: boolean
  onDismiss: () => void
  onConfirm: () => void
  title: string
  message?: React.ReactNode
  steps?: Array<{
    title: string
    description?: string
  }>
  warning?: string
  confirmText?: string
  cancelText?: string
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onDismiss,
  onConfirm,
  title,
  message,
  steps,
  warning,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
}) => {
  const theme = useTheme()

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} maxHeight={90}>
      <Wrapper>
        <RowBetween>
          <div />
          <CloseIcon onClick={onDismiss}>
            <X size={18} />
          </CloseIcon>
        </RowBetween>

        <TitleContainer>
          <MobileResponsiveTitle>{title}</MobileResponsiveTitle>
        </TitleContainer>

        {message && (
          <AutoColumn gap="16px">
            <MobileResponsiveBody textAlign="center" fontSize={14}>
              <div>{message}</div>
            </MobileResponsiveBody>
          </AutoColumn>
        )}

        {steps && steps.length > 0 && (
          <ContentContainer>
            <MobileResponsiveSubHeader>This process will:</MobileResponsiveSubHeader>
            <StepContainer>
              {steps.map((step, index) => (
                <StepItem key={index}>
                  <StepNumber color={theme.deprecated_primary1}>{index + 1}</StepNumber>
                  <StepContent>
                    <MobileResponsiveBody fontWeight={500} fontSize={15}>
                      {step.title}
                    </MobileResponsiveBody>
                    {step.description && (
                      <MobileResponsiveBody fontSize={14} color={theme.textSecondary} marginTop="4px">
                        {step.description}
                      </MobileResponsiveBody>
                    )}
                  </StepContent>
                </StepItem>
              ))}
            </StepContainer>
          </ContentContainer>
        )}

        {warning && (
          <WarningBox>
            <MobileResponsiveBody fontWeight={500} color={theme.deprecated_red1}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertTriangle size={16} />
                Important
              </div>
            </MobileResponsiveBody>
            <MobileResponsiveBody marginTop="4px" fontSize={14}>
              {warning}
            </MobileResponsiveBody>
          </WarningBox>
        )}

        <ButtonsContainer>
          <StyledButtonSecondary onClick={onDismiss}>{cancelText}</StyledButtonSecondary>
          <StyledButtonPrimary onClick={onConfirm}>
            {confirmText}
            <ArrowRight size={16} style={{ marginLeft: '8px' }} />
          </StyledButtonPrimary>
        </ButtonsContainer>
      </Wrapper>
    </Modal>
  )
}

export default ConfirmationModal
